import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import {
  Package, TrendingUp, ShoppingBag, BarChart3, ChevronRight,
  Star, Globe, Users, Layers, Loader2, RefreshCw, CheckCircle2,
  AlertCircle, Mail, Award, MapPin, DollarSign, Calendar, Zap, ArrowRight, MessageSquare
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import TestimonialForm from '../../components/TestimonialForm';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MOCK_SUPPLY_OPPORTUNITIES = [
  { id: 'so_1', event: 'CES Las Vegas 2025', city: 'Las Vegas', country: 'US', category: 'Display Tech', budget_hint: '$8K–$25K', deadline: '2025-02-10', urgency: 'hot' },
  { id: 'so_2', event: 'Hannover Messe 2025', city: 'Hannover', country: 'DE', category: 'Modular Furniture', budget_hint: '€5K–€18K', deadline: '2025-03-01', urgency: 'warm' },
  { id: 'so_3', event: 'Arab Health Dubai', city: 'Dubai', country: 'UAE', category: 'Signage & Print', budget_hint: '$4K–$12K', deadline: '2025-01-28', urgency: 'hot' },
  { id: 'so_4', event: 'Paris Food Exhibition', city: 'Paris', country: 'FR', category: 'Flooring Materials', budget_hint: '€3K–$10K', deadline: '2025-03-15', urgency: 'warm' },
];

const SupplierDashboard = () => {
  const { user, token } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTestimonialForm, setShowTestimonialForm] = useState(false);

  const load = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/supplier/dashboard-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data);
    } catch {
      setStats({ total_products: 0, categories: 0, team_members: 0, plan_code: 'basic', profile_completion: 0, pending_invites: 0 });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [token]); // eslint-disable-line

  const kpis = stats ? [
    { label: t('dashboard.catalogItems'),       value: stats.total_products,     icon: Package,     color: 'bg-purple-50 text-purple-600',   border: 'border-purple-100' },
    { label: t('dashboard.categories'),          value: stats.categories,         icon: Layers,      color: 'bg-blue-50 text-blue-600',       border: 'border-blue-100' },
    { label: t('dashboard.teamMembers'),        value: stats.team_members,       icon: Users,       color: 'bg-green-50 text-green-600',     border: 'border-green-100' },
    { label: t('dashboard.profileCompletion'),  value: `${stats.profile_completion ?? 0}%`, icon: CheckCircle2, color: stats.profile_completion >= 80 ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600', border: stats.profile_completion >= 80 ? 'border-emerald-100' : 'border-amber-100' },
  ] : [];

  const completionColor = stats?.profile_completion >= 80
    ? 'bg-emerald-500' : stats?.profile_completion >= 50
    ? 'bg-amber-500' : 'bg-red-400';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{t('dashboard.title')}</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {t('dashboard.welcomeBack')}, {user?.first_name || 'Supplier'}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={load} className="gap-1.5 h-8">
            <RefreshCw className="h-3.5 w-3.5" /><span className="hidden sm:inline">{t('dashboard.refresh')}</span>
          </Button>
          <Button
            onClick={() => navigate('/app/supplier/profile')}
            className="bg-purple-600 hover:bg-purple-700 text-white gap-2"
          >
            <Globe className="h-4 w-4" /><span className="hidden sm:inline">{t('dashboard.viewProfile')}</span>
          </Button>
        </div>
      </div>

      {/* Plan badge */}
      {stats && (
        <div className="flex items-center gap-2">
          <Badge className="bg-purple-100 text-purple-700 border-purple-200 capitalize">
            {stats.plan_code} plan
          </Badge>
          {stats.pending_invites > 0 && (
            <Badge className="bg-amber-100 text-amber-700 border-amber-200">
              {stats.pending_invites} pending invite{stats.pending_invites > 1 ? 's' : ''}
            </Badge>
          )}
          <span className="text-xs text-gray-400">LIVE data</span>
        </div>
      )}

      {/* Onboarding CTA — show if onboarding not done and completion < 40% */}
      {stats && stats.profile_completion < 40 && !localStorage.getItem('supplier_onboarding_done') && (
        <div className="flex items-center justify-between gap-4 p-4 bg-gradient-to-r from-purple-600 to-purple-700 rounded-2xl text-white shadow-lg">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
              <Award className="h-5 w-5" />
            </div>
            <div>
              <p className="font-bold text-sm">{t('dashboard.completeSupplierProfile')}</p>
              <p className="text-xs text-purple-200 mt-0.5">{t('dashboard.supplierOnboardingDesc')}</p>
            </div>
          </div>
          <Button
            onClick={() => navigate('/app/supplier/onboarding')}
            className="bg-white text-purple-700 hover:bg-purple-50 font-semibold flex-shrink-0 gap-1.5"
            size="sm"
            data-testid="start-onboarding-btn"
          >
            {t('dashboard.startSetup')} <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* KPI Cards */}
      {loading ? (
        <div className="flex items-center justify-center h-32">
          <Loader2 className="animate-spin h-7 w-7 text-purple-400" />
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {kpis.map((k, i) => {
            const Icon = k.icon;
            return (
              <Card key={i} className={`border shadow-sm ${k.border}`} data-testid={`kpi-${k.label.toLowerCase().replace(/ /g, '-')}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{k.label}</p>
                      <p className="text-3xl font-bold text-gray-900 mt-1">{k.value}</p>
                    </div>
                    <div className={`p-2.5 rounded-xl ${k.color}`}><Icon className="h-5 w-5" /></div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Profile completion bar */}
      {stats && (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-gray-700">{t('dashboard.profileCompletion')}</span>
              <span className="text-sm font-bold text-gray-900">{stats.profile_completion ?? 0}%</span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2.5">
              <div
                className={`h-2.5 rounded-full transition-all ${completionColor}`}
                style={{ width: `${stats.profile_completion ?? 0}%` }}
              />
            </div>
            {(stats.profile_completion ?? 0) < 80 && (
              <p className="text-xs text-gray-500 mt-2">
                Complete your profile to attract more exhibitors and contractors.{' '}
                <button onClick={() => navigate('/app/supplier/profile')} className="text-purple-600 font-semibold hover:underline">
                  Complete Now
                </button>
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Charts + Quick actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue placeholder */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3 border-b border-gray-50">
            <CardTitle className="text-sm flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-purple-500" />{t('dashboard.monthlyRevenue')}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="flex items-center justify-center h-[180px] bg-gray-50 rounded-xl">
              <div className="text-center">
                <BarChart3 className="h-8 w-8 text-purple-200 mx-auto mb-2" />
                <p className="text-sm font-semibold text-gray-500">Revenue tracking</p>
                <p className="text-xs text-gray-400 mt-1">Coming soon — order system in development</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3 border-b border-gray-50">
            <CardTitle className="text-sm flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-purple-500" />Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-3">
            {[
              { labelKey: 'manageCatalogue', label: 'Manage Product Catalogue',  icon: Package,    path: '/app/supplier/catalogue', descKey: 'manageCatalogueDesc' },
              { labelKey: 'editProfile',     label: 'Edit Company Profile',       icon: Award,      path: '/app/supplier/profile',   descKey: 'editProfileDesc' },
              { labelKey: 'teamManagement',  label: 'Team Management',            icon: Users,      path: '/app/supplier/team',      descKey: 'teamManagementDesc' },
              { labelKey: 'billingPlans',    label: 'Billing & Plans',            icon: Star,       path: '/app/supplier/billing',   descKey: 'billingPlansDesc' },
            ].map(a => {
              const Icon = a.icon;
              return (
                <button key={a.path}
                  onClick={() => navigate(a.path)}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-gray-50 hover:bg-purple-50 hover:border-purple-200 border border-transparent transition-colors text-left">
                  <div className="w-9 h-9 rounded-lg bg-purple-100 flex items-center justify-center shrink-0">
                    <Icon className="h-4 w-4 text-purple-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800">{a.label}</p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-gray-300 shrink-0" />
                </button>
              );
            })}
          </CardContent>
        </Card>
      </div>

      {/* Tedarik Fırsatları / Supply Opportunities */}
      <Card className="border-0 shadow-sm" data-testid="supply-opportunities-section">
        <CardHeader className="pb-3 border-b border-gray-50">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2">
              <Zap className="h-4 w-4 text-purple-500" />
              {t('dashboard.supplyOpportunities')}
              <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-normal">Live Events</span>
            </CardTitle>
            <Button variant="ghost" size="sm" className="text-xs text-gray-500" onClick={() => navigate('/app/supplier/catalogue')}>
              View Catalogue <ArrowRight className="h-3 w-3 ml-1" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-gray-50">
            {MOCK_SUPPLY_OPPORTUNITIES.map(o => (
              <div key={o.id} className="p-4 hover:bg-gray-50 transition-colors flex items-center gap-3">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  o.urgency === 'hot' ? 'bg-red-100' : 'bg-amber-100'
                }`}>
                  {o.urgency === 'hot'
                    ? <Zap className="h-4 w-4 text-red-600" />
                    : <DollarSign className="h-4 w-4 text-amber-600" />
                  }
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{o.event}</p>
                  <p className="text-xs text-gray-400 flex items-center gap-2 mt-0.5">
                    <MapPin className="h-3 w-3 flex-shrink-0" />{o.city}, {o.country}
                    <span className="text-[10px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded-full">{o.category}</span>
                  </p>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <span className="text-xs font-bold text-gray-700">{o.budget_hint}</span>
                  <span className="text-[10px] text-gray-400 flex items-center gap-1">
                    <Calendar className="h-2.5 w-2.5" />
                    {new Date(o.deadline).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })}
                  </span>
                </div>
                <Button
                  size="sm"
                  onClick={() => navigate('/app/supplier/catalogue')}
                  className="h-7 text-xs bg-purple-600 hover:bg-purple-700 text-white gap-1 shrink-0"
                  data-testid={`supply-opportunity-${o.id}`}
                >
                  <Mail className="h-3 w-3" />Contact
                </Button>
              </div>
            ))}
          </div>
          <div className="px-4 py-3 bg-purple-50/50 border-t border-gray-50 rounded-b-xl">
            <p className="text-xs text-gray-400 text-center">
              Showcase your products to stand builders worldwide — <button onClick={() => navigate('/app/supplier/catalogue')} className="text-purple-600 font-semibold hover:underline">complete your catalogue</button>
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Empty catalog CTA */}
      {stats && stats.total_products === 0 && (
        <Card className="border-0 shadow-sm border-dashed border-2 border-purple-200 bg-purple-50/40">
          <CardContent className="p-6 text-center">
            <Package className="h-10 w-10 text-purple-300 mx-auto mb-3" />
            <p className="font-semibold text-gray-700">Your catalogue is empty</p>
            <p className="text-sm text-gray-400 mt-1 mb-4">Add product categories to start attracting exhibitors and contractors.</p>
            <Button onClick={() => navigate('/app/supplier/catalogue')}
              className="bg-purple-600 hover:bg-purple-700 text-white">
              <Package className="h-4 w-4 mr-2" />Set Up Catalogue
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Deneyimini Paylaş Kartı */}
      {!localStorage.getItem('supplier_testimonial_submitted') && (
        <Card className="border-0 shadow-sm bg-gradient-to-br from-purple-50 to-violet-50 border border-purple-100">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl bg-purple-100 flex items-center justify-center shrink-0">
              <MessageSquare className="h-5 w-5 text-purple-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-gray-900 text-sm">ExpoStand deneyiminizi paylaşın</p>
              <p className="text-xs text-gray-500 mt-0.5">Tedarikçi olarak deneyiminizi anlatın, alıcılara ilham olun.</p>
            </div>
            <Button
              size="sm"
              onClick={() => setShowTestimonialForm(true)}
              className="bg-purple-600 hover:bg-purple-700 text-white shrink-0 gap-1.5"
              data-testid="supplier-write-review-btn"
            >
              <Star className="h-3.5 w-3.5" />
              Yorum Yaz
            </Button>
          </CardContent>
        </Card>
      )}

      {showTestimonialForm && (
        <TestimonialForm
          user={user}
          onClose={() => {
            setShowTestimonialForm(false);
            localStorage.setItem('supplier_testimonial_submitted', '1');
          }}
        />
      )}
    </div>
  );
};

export default SupplierDashboard;
